import * as React from "react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { ChevronDown } from "lucide-react";

import { cn } from "@/lib/utils";

const Accordion = AccordionPrimitive.Root;

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item ref={ref} className={cn("border-b", className)} {...props} />
));
AccordionItem.displayName = "AccordionItem";

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Header className="flex">
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(
        "flex flex-1 items-center justify-between py-4 font-medium transition-all hover:underline [&[data-state=open]>svg]:rotate-180",
        className,
      )}
      {...props}
    >
      {children}
      <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200" />
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
));
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, forceMount, ...props }, ref) =>
  forceMount ? (
    // forceMount keeps a closed panel's text in the DOM, so search engines index
    // every answer, not just the open one. Radix then neither hides a closed
    // panel nor re-measures one as it opens (the height keyframes below would
    // have nothing to animate to), so it collapses with a grid-rows transition
    // instead, on an inner element because Radix zeroes the panel's own
    // transitions while it measures. A closed panel is invisible, so assistive
    // tech skips it as before; the inner element sets its own visibility so
    // the text stays visible while it collapses. The curve is CSS's ease-out,
    // the one the keyframes use (Tailwind's ease-out is a sharper curve).
    <AccordionPrimitive.Content
      ref={ref}
      forceMount
      className="group/panel text-sm data-[state=closed]:invisible"
      {...props}
    >
      <div
        className="visible grid transition-[grid-template-rows,visibility] duration-200 group-data-[state=open]/panel:grid-rows-[1fr] group-data-[state=closed]/panel:invisible group-data-[state=closed]/panel:grid-rows-[0fr]"
        style={{ transitionTimingFunction: "ease-out" }}
      >
        <div className="min-h-0 overflow-hidden">
          <div className={cn("pb-4 pt-0", className)}>{children}</div>
        </div>
      </div>
    </AccordionPrimitive.Content>
  ) : (
    <AccordionPrimitive.Content
      ref={ref}
      className="overflow-hidden text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
      {...props}
    >
      <div className={cn("pb-4 pt-0", className)}>{children}</div>
    </AccordionPrimitive.Content>
  ),
);

AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
